import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiSearch, FiCalendar, FiClock, FiStar, FiDollarSign, FiUsers, FiTrendingUp } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { Card, Button, StatCard, Badge, Avatar, Spinner } from '../components/Components';
import { dashboardAPI, bookingAPI } from '../utils/api';
import './Dashboard.css';

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [upcoming, setUpcoming] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [statsRes, bookingsRes] = await Promise.all([
          dashboardAPI.getStats(),
          bookingAPI.getAll({ time: 'upcoming' }),
        ]);
        setStats(statsRes.data);
        setUpcoming(bookingsRes.data.upcoming?.slice(0, 4) || []);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    load();
  }, []);

  if (loading) return <div className="page-container"><Spinner /></div>;

  const isCoach = user?.role === 'coach';

  const getRelativeDay = (dateStr) => {
    const today = new Date(); today.setHours(0,0,0,0);
    const d = new Date(dateStr + 'T00:00:00');
    const diff = Math.round((d - today) / 86400000);
    if (diff === 0) return 'Today';
    if (diff === 1) return 'Tomorrow';
    if (diff === 2) return 'Day After Tomorrow';
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  return (
    <div className="page-container">
      <div className="dashboard__banner">
        <div className="dashboard__banner-orb" />
        <div className="dashboard__banner-content">
          <h1 className="dashboard__welcome">Hello, {user?.name?.split(' ')[0]}! 👋</h1>
          <p className="dashboard__welcome-sub">
            {isCoach
              ? `You have ${stats?.upcomingSessions || 0} upcoming sessions. Manage your availability to help more people.`
              : `You have ${stats?.upcomingSessions || 0} upcoming sessions. Ready to find your next coach?`}
          </p>
        </div>
      </div>

      <div className="dashboard__stats">
        {isCoach ? (<>
          <StatCard value={stats?.totalSessions || 0} label="Total Sessions" icon={<FiCalendar size={18} />} color="#4f46e5" />
          <StatCard value={`$${stats?.rate || 0}/hr`} label="Hourly Rate" icon={<FiDollarSign size={18} />} color="#059669" />
          <StatCard value={`${stats?.rating || 0}★`} label="Rating" icon={<FiStar size={18} />} color="#f59e0b" />
          <StatCard value={stats?.openSlots || 0} label="Open Slots" icon={<FiClock size={18} />} color="#8b5cf6" />
        </>) : (<>
          <StatCard value={stats?.upcomingSessions || 0} label="Upcoming" icon={<FiCalendar size={18} />} color="#4f46e5" />
          <StatCard value={stats?.totalBookings || 0} label="Total Bookings" icon={<FiTrendingUp size={18} />} color="#059669" />
          <StatCard value={stats?.totalCoaches || 0} label="Coaches" icon={<FiUsers size={18} />} color="#f59e0b" />
        </>)}
      </div>

      <div className="dashboard__actions">
        {isCoach ? (<>
          <Card className="dashboard__action-card" hover onClick={() => navigate('/availability')}>
            <div className="dashboard__action-icon">🗓️</div>
            <h3 className="dashboard__action-title">Manage Availability</h3>
            <p className="dashboard__action-desc">Set your slots for the next 2 weeks</p>
          </Card>
          <Card className="dashboard__action-card" hover onClick={() => navigate('/bookings')}>
            <div className="dashboard__action-icon">📋</div>
            <h3 className="dashboard__action-title">Booked Sessions</h3>
            <p className="dashboard__action-desc">View sessions booked with you</p>
          </Card>
          <Card className="dashboard__action-card" hover onClick={() => navigate('/profile')}>
            <div className="dashboard__action-icon">✏️</div>
            <h3 className="dashboard__action-title">Edit Profile</h3>
            <p className="dashboard__action-desc">Update expertise and interests</p>
          </Card>
        </>) : (<>
          <Card className="dashboard__action-card" hover onClick={() => navigate('/search')}>
            <div className="dashboard__action-icon">🔍</div>
            <h3 className="dashboard__action-title">Find a Coach</h3>
            <p className="dashboard__action-desc">Search by area of interest</p>
          </Card>
          <Card className="dashboard__action-card" hover onClick={() => navigate('/bookings')}>
            <div className="dashboard__action-icon">📅</div>
            <h3 className="dashboard__action-title">My Sessions</h3>
            <p className="dashboard__action-desc">View booked coaching sessions</p>
          </Card>
          <Card className="dashboard__action-card" hover onClick={() => navigate('/profile')}>
            <div className="dashboard__action-icon">👤</div>
            <h3 className="dashboard__action-title">My Profile</h3>
            <p className="dashboard__action-desc">Update your interests & goals</p>
          </Card>
        </>)}
      </div>

      {upcoming.length > 0 && (
        <div className="dashboard__upcoming">
          <h3 className="section-title">Upcoming Sessions</h3>
          <div className="dashboard__upcoming-list">
            {upcoming.map((b) => (
              <Card key={b._id} className="dashboard__booking-card">
                <div className="dashboard__booking-left">
                  <Avatar initials={isCoach ? (b.candidateName?.split(' ').map(n=>n[0]).join('') || '?') : (b.coachAvatar || b.coachName?.split(' ').map(n=>n[0]).join('') || '?')} size="md" />
                  <div>
                    <div className="dashboard__booking-name">{isCoach ? b.candidateName : b.coachName}</div>
                    <Badge variant="primary" size="sm">{b.coachExpertise?.[0] || b.topic || 'Session'}</Badge>
                  </div>
                </div>
                <div className="dashboard__booking-right">
                  <span className={`dashboard__booking-day ${getRelativeDay(b.date) === 'Today' ? 'dashboard__booking-day--today' : ''}`}>
                    {getRelativeDay(b.date)}
                  </span>
                  <span className="dashboard__booking-time">{b.time}</span>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
